import { useMutation } from "@apollo/client";
import { create, show, useModal } from "@ebay/nice-modal-react";

import FlashContext from "components/FlashMessages/FlashContext";
import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import useTypedContext from "hooks/useTypedContext";
import { AuthorizationScheme } from "types/generated";

import { UPDATE_ACCOUNT_AUTH_SCHEME } from "./gql";
import { humanizeAuthorizationSchemeName } from "./helpers";

type ChangeManagementStrategyConfirmationModalProps = {
  scheme: AuthorizationScheme;
  name: string;
  onSwitched: () => void;
};

const ChangeManagementStrategyConfirmationModal = create(
  function ChangeManagementStrategyConfirmationModal({
    scheme,
    name,
    onSwitched,
  }: ChangeManagementStrategyConfirmationModalProps) {
    const modal = useModal();
    const { onError, reportSuccess } = useTypedContext(FlashContext);

    const [updateAccountAuthScheme, { loading }] = useMutation(UPDATE_ACCOUNT_AUTH_SCHEME, {
      refetchQueries: ["GetAuthorizationScheme"],
    });

    const handleOnConfirm = () => {
      updateAccountAuthScheme({ variables: { schema: scheme } })
        .then(({ data: { accountUpdateAuthorizationScheme } }) => {
          reportSuccess({
            message: `Management strategy was changed to ${humanizeAuthorizationSchemeName(
              accountUpdateAuthorizationScheme
            )}`,
          });
          onSwitched();
          modal.resolve();
          modal.hide();
        })
        .catch(onError);
    };

    return (
      <ConfirmationModal
        title="Switch management strategy"
        confirmationButtonLabel="Switch strategy"
        confirmationButtonVariant="primary"
        onConfirm={handleOnConfirm}
        isLoading={loading}
        isDismissable={!loading}
      >
        <ConfirmationModalMessage>
          After switching to {name}, all active sessions (except the current one) will be
          invalidated.
          <br />
          <br />
          Are you sure you want to switch to <strong>{name}</strong>?
        </ConfirmationModalMessage>
      </ConfirmationModal>
    );
  }
);

export const showChangeManagementStrategyConfirmationModal = (
  props: ChangeManagementStrategyConfirmationModalProps
) => show(ChangeManagementStrategyConfirmationModal, props);
